import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, HandCoins, Receipt, Factory, Users, Wallet, Package, CalendarRange, FileText, Settings, LogOut, Menu, CalendarDays } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { longDate, todayJkt } from "@/lib/api";
import { SimBanner } from "@/components/SimActions";

export const NAV = [
  { to: "/", label: "Beranda", icon: Home, id: "beranda" },
  { to: "/pemasukan", label: "Pemasukan", icon: HandCoins, id: "pemasukan" },
  { to: "/pengeluaran", label: "Pengeluaran", icon: Receipt, id: "pengeluaran" },
  { to: "/kasbon-pabrik", label: "Kasbon Pabrik", icon: Factory, id: "kasbon-pabrik" },
  { to: "/kasbon-pelanggan", label: "Kasbon Pelanggan", icon: Users, id: "kasbon-pelanggan" },
  { to: "/uang-kas", label: "Uang Kas", icon: Wallet, id: "uang-kas" },
  { to: "/stok", label: "Stok Bahan Baku", icon: Package, id: "stok" },
];
export const REPORTS = [
  { to: "/rekap-mingguan", label: "Rekap Mingguan", icon: CalendarRange, id: "rekap-mingguan" },
  { to: "/laporan-bulanan", label: "Laporan Bulanan", icon: FileText, id: "laporan-bulanan" },
];

const Item = ({ to, label, icon: I, id, onClick }) => (
  <NavLink to={to} end={to === "/"} onClick={onClick} data-testid={`nav-${id}`}
    className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[15px] font-semibold transition-colors ${isActive ? "bg-[#1f7a46] text-white shadow-inner" : "text-white/80 hover:bg-white/10 hover:text-white"}`}>
    <I className="w-5 h-5 shrink-0" /> {label}
  </NavLink>
);

function SideContent({ onNav }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="h-full flex flex-col bg-[var(--jt-emerald)] text-white">
      <div className="flex items-center gap-3 px-5 py-5">
        <img src="/logo-256.png" alt="Logo Juragan Tahu" className="w-11 h-11 object-contain rounded-xl" data-testid="sidebar-logo" />
        <div className="font-brand text-xl font-semibold leading-tight">Juragan Tahu</div>
      </div>
      <nav className="px-3 space-y-1 flex-1 overflow-y-auto">
        {NAV.map((n) => <Item key={n.to} {...n} onClick={onNav} />)}
        <div className="my-4 h-px bg-white/10 mx-2" />
        {REPORTS.map((n) => <Item key={n.to} {...n} onClick={onNav} />)}
      </nav>
      <div className="p-3 space-y-2">
        <button onClick={() => { nav("/pengaturan"); onNav?.(); }} data-testid="sidebar-settings-button"
          className="w-full flex items-center gap-3 rounded-xl bg-white/[0.07] hover:bg-white/[0.12] px-3.5 py-3 text-left transition-colors">
          <Settings className="w-5 h-5 text-[#e3c272]" />
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" data-testid="sidebar-username">{user?.username}</div>
            <div className="text-xs text-white/60">Pengaturan Usaha</div>
          </div>
        </button>
        <button onClick={logout} data-testid="logout-button"
          className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-white/80 hover:bg-white/10">
          <LogOut className="w-5 h-5" /> Keluar
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const current = [...NAV, ...REPORTS].find((n) => n.to === pathname)?.label || "Pengaturan";
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block w-64 shrink-0 sticky top-0 h-screen no-print"><SideContent /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 border-0 bg-[var(--jt-emerald)]">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SideContent onNav={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-[var(--jt-cream)]/90 backdrop-blur-md border-b border-[var(--jt-gold)]/15 no-print">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-10 h-16">
            <button className="lg:hidden w-11 h-11 -ml-1 flex items-center justify-center rounded-xl hover:bg-black/5" onClick={() => setOpen(true)} aria-label="Buka menu" data-testid="mobile-menu-button">
              <Menu className="w-6 h-6 text-[var(--jt-emerald)]" />
            </button>
            <img src="/logo-256.png" alt="Logo Juragan Tahu" className="lg:hidden w-9 h-9 object-contain rounded-lg" />
            <h1 className="text-lg sm:text-xl font-extrabold text-[var(--jt-emerald)] truncate" data-testid="page-title">{current}</h1>
            <div className="ml-auto hidden sm:flex items-center gap-2 text-sm text-muted-foreground border rounded-xl px-3 py-2 bg-white/70" data-testid="header-date">
              <CalendarDays className="w-4 h-4" /> {longDate(todayJkt())}
            </div>
          </div>
        </header>
        <SimBanner />
        <main className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 max-w-[1600px] print-full"><Outlet /></main>
      </div>
    </div>
  );
}
